import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { tasksService, type Task, type TaskInput } from '../../../services/projectManagement/tasksService';

interface SubtasksPanelProps {
  parentTask: Task;
  organizationId: string;
  onUpdate: () => void;
}

const SubtasksPanel: React.FC<SubtasksPanelProps> = ({ parentTask, organizationId, onUpdate }) => {
  const [subtasks, setSubtasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('');

  useEffect(() => {
    void fetchSubtasks();
  }, [parentTask.id]);

  const fetchSubtasks = async () => {
    setLoading(true);
    try {
      const { data, error } = await tasksService.list({
        organization_id: organizationId,
        parent_task_id: parentTask.id,
      });

      if (error) {
        toast.error('Failed to load subtasks');
      } else {
        setSubtasks(data || []);
      }
    } catch (err) {
      toast.error('Failed to load subtasks');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubtask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSubtaskTitle.trim()) return;

    try {
      const subtaskData: TaskInput = {
        title: newSubtaskTitle.trim(),
        project_id: parentTask.project_id,
        organization_id: organizationId,
        parent_task_id: parentTask.id,
        status: 'todo',
        priority: 'medium',
        estimated_time: 0,
      };

      const { error } = await tasksService.create(subtaskData);

      if (error) {
        toast.error('Failed to create subtask');
      } else {
        toast.success('Subtask created successfully');
        setNewSubtaskTitle('');
        setShowAddForm(false);
        await fetchSubtasks();
        onUpdate();
      }
    } catch (err) {
      toast.error('Failed to create subtask');
    }
  };

  const handleToggleSubtask = async (subtask: Task) => {
    const newStatus = subtask.status === 'done' ? 'todo' : 'done';

    try {
      const { error } = await tasksService.update(subtask.id, organizationId, {
        status: newStatus,
      });

      if (error) {
        toast.error('Failed to update subtask');
      } else {
        await fetchSubtasks();
        onUpdate();
      }
    } catch (err) {
      toast.error('Failed to update subtask');
    }
  };

  const handleDelete = async (subtaskId: string) => {
    if (!window.confirm('Are you sure you want to delete this subtask?')) return;

    try {
      const { error } = await tasksService.delete(subtaskId, organizationId);

      if (error) {
        toast.error('Failed to delete subtask');
      } else {
        toast.success('Subtask deleted');
        await fetchSubtasks();
        onUpdate();
      }
    } catch (err) {
      toast.error('Failed to delete subtask');
    }
  };

  const completedCount = subtasks.filter(st => st.status === 'done').length;
  const progressPercentage = subtasks.length > 0 ? (completedCount / subtasks.length) * 100 : 0;

  if (loading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="text-sm text-gray-500">Loading subtasks...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Progress */}
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h4 className="text-sm font-semibold text-gray-900">
              Subtasks ({completedCount}/{subtasks.length})
            </h4>
            {subtasks.length > 0 && (
              <span className="text-xs text-gray-500">
                {progressPercentage.toFixed(0)}% complete
              </span>
            )}
          </div>
          {subtasks.length > 0 && (
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-200">
              <div
                className="h-full bg-green-500 transition-all"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          )}
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="ml-4 rounded-lg bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700 hover:bg-blue-100"
        >
          {showAddForm ? 'Cancel' : '+ Add Subtask'}
        </button>
      </div>

      {/* Add Subtask Form */}
      {showAddForm && (
        <form onSubmit={handleAddSubtask} className="rounded-lg border border-blue-200 bg-blue-50 p-3">
          <input
            type="text"
            value={newSubtaskTitle}
            onChange={(e) => setNewSubtaskTitle(e.target.value)}
            placeholder="Subtask title..."
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            autoFocus
          />
          <div className="mt-2 flex gap-2">
            <button
              type="submit"
              className="rounded-md bg-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-blue-700"
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => {
                setShowAddForm(false);
                setNewSubtaskTitle('');
              }}
              className="rounded-md bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 border border-gray-300"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Subtasks List */}
      {subtasks.length > 0 ? (
        <div className="space-y-2">
          {subtasks.map((subtask) => (
            <div
              key={subtask.id}
              className="group flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 hover:bg-gray-50"
            >
              {/* Checkbox */}
              <button
                onClick={() => void handleToggleSubtask(subtask)}
                className="flex-shrink-0"
              >
                <div
                  className={`h-5 w-5 rounded border-2 flex items-center justify-center transition-colors ${
                    subtask.status === 'done'
                      ? 'bg-green-500 border-green-500'
                      : 'border-gray-300 hover:border-green-500'
                  }`}
                >
                  {subtask.status === 'done' && (
                    <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </button>

              {/* Title */}
              <div className="flex-1 min-w-0">
                <p
                  className={`text-sm ${
                    subtask.status === 'done'
                      ? 'text-gray-400 line-through'
                      : 'text-gray-900'
                  }`}
                >
                  {subtask.title}
                </p>
                {subtask.description && (
                  <p className="mt-1 text-xs text-gray-500 truncate">
                    {subtask.description}
                  </p>
                )}
              </div>

              {/* Priority Badge */}
              {subtask.priority !== 'medium' && (
                <span
                  className={`flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${
                    subtask.priority === 'urgent'
                      ? 'bg-red-100 text-red-800'
                      : subtask.priority === 'high'
                      ? 'bg-orange-100 text-orange-800'
                      : 'bg-green-100 text-green-800'
                  }`}
                >
                  {subtask.priority}
                </span>
              )}

              {/* Delete Button */}
              <button
                onClick={() => void handleDelete(subtask.id)}
                className="flex-shrink-0 opacity-0 group-hover:opacity-100 text-red-600 hover:text-red-800 transition-opacity"
                title="Delete subtask"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      ) : (
        !showAddForm && (
          <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-8 text-center">
            <div className="text-3xl mb-2">✓</div>
            <p className="text-sm text-gray-600">No subtasks yet</p>
            <p className="text-xs text-gray-500 mt-1">
              Break down this task into smaller subtasks
            </p>
          </div>
        )
      )}
    </div>
  );
};

export default SubtasksPanel;
